import React, { useState, useContext, lazy, Suspense, useEffect } from 'react';
import { CButton, CContainer, CRow, CCol, CCardGroup, CCard, CCardBody, CSpinner, CBadge, CModal, CModalHeader, CModalTitle, CModalBody, CProgress, CProgressBar } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import '../../../scss/_login.scss';
import gl from '../../../assets/icons/android_light_rd_na@2x.png';
import al from '../../../assets/icons/applenorm.png';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { withFirebase } from '../../firebase';
import logo from '../../../assets/mierosloginlogo0.jpg';
import { AuthUserContext } from '../../session';
import packageJson from '../../../../package.json';
import config from '../../../utils/EnvironmentSwitcher';
// Firebase v9 imports
import { getAuth, signInWithCustomToken, signInWithCredential, FacebookAuthProvider, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { getDatabase, ref, get, set } from 'firebase/database';
import { TwitterAuthProvider } from 'firebase/auth';
import cibTwitter from '../../../assets/icons/logo-black.png';
import fbIcon from '../../../assets/icons/facebook.png';
import emailIcon from '../../../assets/icons/email.png';

// Extract version from package.json
const { version } = packageJson;


// Lazy loading components
const LocalLogin = lazy(() => import('./LocalLoginWallet'));
const GoogleLogin = lazy(() => import('./GoogleLogin'));
const AppleLogin = lazy(() => import('./AppleSignIn'));
// Initialize modules

// Firebase setup
const generateSecurePassword = (length = 12) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => ('0' + byte.toString(16)).slice(-2)).join('').slice(0, length);
};
const auth = getAuth();
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
appleProvider.setCustomParameters({
  clientId: 'cloud.mieros.mie', // Your Services ID
  redirectUri: 'https://mieros-us.firebaseapp.com/__/auth/handler', // Ensure this matches exactly
});
appleProvider.addScope('email');
appleProvider.addScope('name');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.activity.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.location.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.body.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.nutrition.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.blood_pressure.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.blood_glucose.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.oxygen_saturation.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.body_temperature.read');
googleProvider.addScope('https://www.googleapis.com/auth/fitness.sleep.read');
googleProvider.addScope('https://www.googleapis.com/auth/youtube.readonly');
googleProvider.addScope('https://www.googleapis.com/auth/youtube.force-ssl');
googleProvider.addScope('https://www.googleapis.com/auth/calendar.readonly');
googleProvider.addScope('https://www.googleapis.com/auth/calendar.events.readonly');

// Add these scopes for Google Home/Nest
googleProvider.addScope('https://www.googleapis.com/auth/sdm.service');  // For Smart Device Management

const database = getDatabase(); // Initialize the Realtime Database
// Prefer Vite MODE (same rule as EnvironmentSwitcher) so .env.local cannot force "development" into a prod build.
const environment = import.meta.env.MODE === 'production'
  ? 'production'
  : (import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development');
const SITE_LOCK_ENABLED = ['1', 'true', 'yes', 'on'].includes(
  String(import.meta.env.VITE_SITE_LOCK_ENABLED ?? 'true').toLowerCase()
);


const Login = () => {
  // console.log("Login component mounted"); // Add this line to check if the component is being mounted
  const [showLocalLogin, setShowLocalLogin] = useState(true);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  // Site unlock lock (password-protected gate before login)
  const [siteLocked, setSiteLocked] = useState(SITE_LOCK_ENABLED);
  const [unlockPassword, setUnlockPassword] = useState('');
  const LOCK_PASSWORD = import.meta.env.VITE_SITE_LOCK_PASSWORD || 'mie123';
  const [registrationProgress, setRegistrationProgress] = useState({
    isRegistering: false,
    currentStep: '',
    progress: 0,
    steps: []
  });
  const securePassword = generateSecurePassword();
  const { setAuthUser, setAuthMethod } = useContext(AuthUserContext);
  const [fhirToken, setFhirToken] = useState(null);
  const { authUser } = useContext(AuthUserContext);
  const [animationSpeed, setAnimationSpeed] = useState(1);

  // Page initialization effect - preload images and check auth state
  useEffect(() => {
    const initializePage = async () => {
      try {
        // Preload all login button images
        const imageUrls = [logo, gl, al, cibTwitter, fbIcon, emailIcon];
        const imagePromises = imageUrls.map(src => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve; // Don't block on image errors
            img.src = src;
          });
        });

        // Wait for images to load (with timeout)
        await Promise.race([
          Promise.all(imagePromises),
          new Promise(resolve => setTimeout(resolve, 3000)) // Max 3 second wait
        ]);

        // Small delay to ensure smooth transition
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setPageReady(true);
      } catch (error) {
        console.error('Error initializing login page:', error);
        setPageReady(true); // Show page even if initialization fails
      }
    };

    initializePage();
  }, []);

  useEffect(() => {
    if (authUser && fhirToken) {
      navigate('/dashboard');
    }
  }, [authUser, fhirToken, navigate]);

  // Registration progress helper functions
  const startRegistration = (provider) => {
    const steps = [
      'Authenticating with ' + provider,
      'Checking user account',
      'Creating user profile',
      'Registering with backend',
      'Awarding welcome coins',
      'Finalizing setup'
    ];
    
    setRegistrationProgress({
      isRegistering: true,
      currentStep: steps[0],
      progress: 0,
      steps: steps
    });
  };

  const updateRegistrationStep = (stepIndex, customMessage = null) => {
    setRegistrationProgress(prev => {
      const newStep = customMessage || prev.steps[stepIndex];
      const newProgress = ((stepIndex + 1) / prev.steps.length) * 100;
      
      return {
        ...prev,
        currentStep: newStep,
        progress: Math.min(newProgress, 100)
      };
    });
  };

  const completeRegistration = () => {
    setRegistrationProgress(prev => ({
      ...prev,
      currentStep: 'Registration complete! Redirecting...',
      progress: 100
    }));
    
    // Clear registration state after a short delay
    setTimeout(() => {
      setRegistrationProgress({
        isRegistering: false,
        currentStep: '',
        progress: 0,
        steps: []
      });
    }, 2000);
  };

  const syncOnboardingFromAgents = async (idToken, uid) => {
    try {
      if (!idToken || !uid) return;
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      const count = Array.isArray(data)
        ? data.length
        : Array.isArray(data?.agents)
          ? data.agents.length
          : Array.isArray(data?.items)
            ? data.items.length
            : typeof data?.count === 'number'
              ? data.count
              : 0;
      if (count > 0) {
        localStorage.setItem(`mie_has_agents_${uid}`, 'true');
        await set(ref(database, `users/${uid}/onboardingComplete`), true);
      }
    } catch (err) {
      console.warn('Login: failed to sync onboarding from agents', err);
    }
  };

  const failRegistration = (errorMessage) => {
    setRegistrationProgress(prev => ({
      ...prev,
      currentStep: `Registration failed: ${errorMessage}`,
      progress: 0
    }));
    
    // Clear registration state after error display
    setTimeout(() => {
      setRegistrationProgress({
        isRegistering: false,
        currentStep: '',
        progress: 0,
        steps: []
      });
    }, 3000);
  };

  const registerUserBestEffort = async (idToken, body = {}) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/registerUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        return { ok: true, data: await response.json().catch(() => ({})) };
      }
      const text = await response.text().catch(() => '');
      let parsed = null;
      try { parsed = text ? JSON.parse(text) : null; } catch (_) { parsed = null; }
      return {
        ok: false,
        status: response.status,
        message: parsed?.message || parsed?.error || text || `registerUser failed (${response.status})`,
      };
    } catch (err) {
      return { ok: false, status: 0, message: err?.message || 'registerUser network error' };
    }
  };

  const exchangeFhirTokenViaApi = async (idToken) => {
    const response = await fetch(`${config.apiUrl}/api/fhir/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ firebaseToken: idToken }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `FHIR token exchange failed (${response.status})`);
    }
    const data = await response.json();
    const token = data?.fhirToken;
    if (!token) throw new Error('FHIR token exchange returned empty token');
    return token;
  };

  useEffect(() => {
    // Start animations when component mounts
    const buttons = document.querySelectorAll('.circle-button');
    buttons.forEach(button => {
      button.style.animationPlayState = 'running';
    });

    return () => {
      // Clean up animations when component unmounts
      buttons.forEach(button => {
        button.style.animationPlayState = 'paused';
      });
    };
  }, []);

  const handleMouseEnter = (e) => {
    e.currentTarget.style.animationPlayState = 'paused';
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.animationPlayState = 'running';
  };

  const handleGoogleLogin = async (event) => {
    event.preventDefault();
    //console.log("Google Login - Start");
    startRegistration('Google');
    
    try {
      const socialAuthUser = await signInWithPopup(auth, googleProvider);
      const user = socialAuthUser.user;
      const token = await user.getIdToken();
      const googleAccessToken = socialAuthUser._tokenResponse.oauthAccessToken;
      const expiresIn = socialAuthUser._tokenResponse.expiresIn * 1000; // expiresIn is in seconds, convert to milliseconds
      const expirationTime = Date.now() + expiresIn; // Current time + token validity duration

      //console.log("Google Login - User authenticated:", user);
      updateRegistrationStep(1); // Checking user account

      const googleUserData = {
        miePhoto: user.photoURL,
        mieGoogleToken: token,
        mieDisplayName: user.displayName,
        mieUid: user.uid,
        mieWalletExists: socialAuthUser.mieWalletExists,
        mieEmail: user.email,
        password: securePassword,
        verified: user.emailVerified,
      };

      // Store access tokens
      localStorage.setItem('googleAccessToken', googleAccessToken);
      localStorage.setItem('tokenExpiryTime', expirationTime);
      localStorage.setItem('fbAccessToken', token);

      // Check if user exists in Firebase Database
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        //console.log("Firebase user exists, fetching data...");
        const firebaseUserData = snapshot.val();
        const userSessionData = {
          ...googleUserData,
          ...firebaseUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      } else {
        console.log("User does not exist in Firebase, creating new user...");
        updateRegistrationStep(2); // Creating user profile
        
        const newUserData = {
          email: user.email,
          name: user.displayName,
          phone: "TBD",
          address: "TBD",
          paymentID: "0",
          userID: user.uid,
          password: securePassword,
          userType: "TRD",
          mieWalletExists: false,
          mieUserID: user.uid,
          verified: user.emailVerified,
        };

        await set(userRef, newUserData);
        const userSessionData = {
          ...googleUserData,
          ...newUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      }

      toast("Google Login - User session stored successfully, checking your account...");
      updateRegistrationStep(3); // Registering with backend
      
      // Get Firebase ID token
      //const idToken = await user.getIdToken();
      // config comes from EnvironmentSwitcher (reads .env.development / .env.production)
      if (!config?.fhirUrl || !config?.apiUrl) {
        toast.error("Configuration URLs are not defined. Check .env.development / .env.production and VITE_APP_ENV.");
        throw new Error('Configuration URLs are not defined');
      }
      // Best-effort register: do not block login on Fabric CA issues.
      const registerResult = await registerUserBestEffort(token);
      if (registerResult.ok) {
        updateRegistrationStep(4); // Awarding welcome coins
        toast.success("User registered and awarded 100 coins successfully");
        console.log('User registered and awarded 100 coins successfully');
        
        updateRegistrationStep(5); // Finalizing setup
        setTimeout(() => completeRegistration(), 500);
      } else {
        console.warn('Login: registerUser failed (continuing):', registerResult.message);
        toast.warn(`Blockchain registration skipped: ${registerResult.message}`);
      }

      // Exchange Firebase token for FHIR token through API proxy (avoids browser CORS with FHIR auth endpoint)
      const fhirAccessToken = await exchangeFhirTokenViaApi(token);
      setFhirToken(fhirAccessToken);
      localStorage.setItem('mieFToken', fhirAccessToken);

      toast.success("Login successful!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      // After successfully logging in and setting tokens
      // wait until `authUser` is set before navigating
      // Listen for auth state change to ensure authUser is set
      // Update authUser in context
      setAuthUser(user); // Update context immediately after login
      await syncOnboardingFromAgents(token, user.uid);
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          navigate('/dashboard');
          unsubscribe(); // Stop listening once the user is authenticated
        }
      });
    } catch (error) {
      console.error("Error during Google login:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 5000,
        containerId: "main-toast"
      });
      failRegistration(error.message);
    }
  };

  const handleAppleLogin = async (event) => {
    event.preventDefault();
    console.log("Apple Login - Start");
    startRegistration('Apple');

    try {
      // Initialize Apple OAuthProvider
      // Perform sign-in with popup
      //console.log("Auth: ",JSON.stringify(auth), " applePRov: ", JSON.stringify(appleProvider));
      const socialAuthUser = await signInWithPopup(auth, appleProvider);
      //console.log("Apple Login - Start0");
      const user = socialAuthUser.user;
      const token = await user.getIdToken();
      //console.log("SocialApp: ", JSON.stringify(socialAuthUser));

      // Apple credential
      const credential = OAuthProvider.credentialFromResult(socialAuthUser);
      if (credential) {
        const appleAccessToken = credential.accessToken;
        const idToken = credential.idToken;

        // console.log("Apple Access Token:", appleAccessToken);
        //  console.log("Apple ID Token:", idToken);

        // Log extracted details
        // console.log("Apple Login - User authenticated:", user);

        // Prepare user data for session
        updateRegistrationStep(1); // Checking user account
        
        const appleUserData = {
          miePhoto: user.photoURL, // Apple doesn't provide photoURL; this may remain null
          mieAppleToken: idToken,
          mieDisplayName: user.displayName, // Apple only shares displayName on first login
          mieUid: user.uid,
          mieWalletExists: socialAuthUser.mieWalletExists,
          mieEmail: user.email,
          password: securePassword, // Assuming `securePassword` is defined elsewhere
          verified: user.emailVerified,
        };

        // Store tokens in localStorage
        localStorage.setItem('appleAccessToken', appleAccessToken);

        // Check if user exists in Firebase Realtime Database
        const userRef = ref(database, `users/${user.uid}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          // User exists; fetch and merge session data
          console.log("Firebase user exists, fetching data...");
          const firebaseUserData = snapshot.val();
          const userSessionData = {
            ...appleUserData,
            ...firebaseUserData,
          };
          localStorage.setItem('userSession', JSON.stringify(userSessionData));
        } else {
          // User does not exist; create a new user in Firebase
          console.log("User does not exist in Firebase, creating new user...");
          updateRegistrationStep(2); // Creating user profile
          
          const newUserData = {
            email: user.email,
            name: user.displayName || "TBD",
            phone: "TBD",
            address: "TBD",
            paymentID: "0",
            userID: user.uid,
            password: securePassword, // Assuming `securePassword` is defined elsewhere
            userType: "TRD",
            mieWalletExists: false,
            mieUserID: user.uid,
            verified: user.emailVerified,
          };

          await set(userRef, newUserData);
          const userSessionData = {
            ...appleUserData,
            ...newUserData,
          };
          localStorage.setItem('userSession', JSON.stringify(userSessionData));
        }

        console.log("Apple Login - User session stored");
        updateRegistrationStep(3); // Registering with backend

        const registerResult = await registerUserBestEffort(token);
        if (registerResult.ok) {
          updateRegistrationStep(4); // Awarding welcome coins
          console.log('User registered and awarded 100 coins successfully');
          
          updateRegistrationStep(5); // Finalizing setup
          setTimeout(() => completeRegistration(), 500);
        } else {
          console.warn('Apple login: registerUser failed (continuing):', registerResult.message);
          toast.warn(`Blockchain registration skipped: ${registerResult.message}`);
        }

        const fhirAccessToken = await exchangeFhirTokenViaApi(token);
        setFhirToken(fhirAccessToken);
        localStorage.setItem('mieFToken', fhirAccessToken);

        toast.success("Login successful!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light"
        });

        // Set authUser context and navigate after login
        setAuthUser(user); // Update context immediately after login
        await syncOnboardingFromAgents(token, user.uid);
        const unsubscribe = auth.onAuthStateChanged((authUser) => {
          if (authUser) {
            navigate('/dashboard');
            unsubscribe(); // Stop listening once the user is authenticated
          }
        });
      }
    } catch (error) {
      console.error("Error during Apple login:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 5000,
        containerId: "main-toast"
      });
      failRegistration(error.message);
    }
  };

  const handleTwitterLogin = async () => {
    try {
      const provider = new TwitterAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log("Twitter Login - Result: ", result);
      const user = result.user;
      const token = await user.getIdToken();

      // Store user data
      const twitterUserData = {
        miePhoto: user.photoURL,
        mieTwitterToken: token,
        mieDisplayName: user.displayName,
        mieUid: user.uid,
        mieWalletExists: false,
        mieEmail: user.email,
        password: securePassword,
        verified: user.emailVerified,
      };

      // Check if user exists in Firebase Database
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const firebaseUserData = snapshot.val();
        const userSessionData = {
          ...twitterUserData,
          ...firebaseUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      } else {
        // Create new user in Firebase
        const newUserData = {
          email: user.email,
          name: user.displayName,
          phone: "TBD",
          address: "TBD",
          paymentID: "0",
          userID: user.uid,
          password: securePassword,
          userType: "TRD",
          mieWalletExists: false,
          mieUserID: user.uid,
          verified: user.emailVerified,
        };

        await set(userRef, newUserData);
        const userSessionData = {
          ...twitterUserData,
          ...newUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));

        // Show welcome toast for new users
        toast.success(
          "Welcome to MiEROS! You've been awarded 100 coins for registering.",
          {
            duration: 3000,
            icon: '🎉'
          }
        );
      }

      const registerResult = await registerUserBestEffort(token);
      if (registerResult.ok) {
        if (!snapshot.exists()) {
          console.log('User registered and awarded 100 coins successfully');
        }
      } else {
        console.warn('Twitter login: registerUser failed (continuing):', registerResult.message);
      }

      const fhirAccessToken = await exchangeFhirTokenViaApi(token);
      setFhirToken(fhirAccessToken);
      localStorage.setItem('mieFToken', fhirAccessToken);

      toast.success("Login successful!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });

      // Update auth context and navigate
      setAuthUser(user);
      await syncOnboardingFromAgents(token, user.uid);
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          navigate('/dashboard');
          unsubscribe();
        }
      });
    } catch (error) {
      console.error('Twitter login error:', error);
      toast.error('Twitter login failed. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        containerId: "main-toast"
      });
    }
  };

  const handleFacebookLogin = async (event) => {
    event.preventDefault();
    try {
      const provider = new FacebookAuthProvider();
      const socialAuthUser = await signInWithPopup(auth, provider);
      const user = socialAuthUser.user;
      const token = await user.getIdToken();
      const credential = FacebookAuthProvider.credentialFromResult(socialAuthUser);
      const fbAccessToken = credential.accessToken;

      // Store user data
      const facebookUserData = {
        miePhoto: user.photoURL,
        mieFacebookToken: token,
        mieDisplayName: user.displayName,
        mieUid: user.uid,
        mieWalletExists: socialAuthUser.mieWalletExists,
        mieEmail: user.email,
        password: securePassword,
        verified: user.emailVerified,
      };

      // Store tokens
      localStorage.setItem('fbAccessToken', fbAccessToken);
      localStorage.setItem('fbIdToken', token);

      // Check if user exists in Firebase Database
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const firebaseUserData = snapshot.val();
        const userSessionData = {
          ...facebookUserData,
          ...firebaseUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      } else {
        // Create new user in Firebase
        const newUserData = {
          email: user.email,
          name: user.displayName,
          phone: "TBD",
          address: "TBD",
          paymentID: "0",
          userID: user.uid,
          password: securePassword,
          userType: "TRD",
          mieWalletExists: false,
          mieUserID: user.uid,
          verified: user.emailVerified,
        };

        await set(userRef, newUserData);
        const userSessionData = {
          ...facebookUserData,
          ...newUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      }

      const registerResult = await registerUserBestEffort(token);
      if (registerResult.ok) {
        console.log('User registered and awarded 100 coins successfully');
      } else {
        console.warn('Facebook login: registerUser failed (continuing):', registerResult.message);
      }

      const fhirAccessToken = await exchangeFhirTokenViaApi(token);
      setFhirToken(fhirAccessToken);
      localStorage.setItem('mieFToken', fhirAccessToken);

      toast.success("Login successful!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });

      // Update auth context and navigate
      setAuthUser(user);
      await syncOnboardingFromAgents(token, user.uid);
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          navigate('/dashboard');
          unsubscribe();
        }
      });
    } catch (error) {
      console.error("Error during Facebook login:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 5000,
        containerId: "main-toast"
      });
    }
  };



  // Show loading screen while page initializes
  if (!pageReady) {
    return (
      <div className="mieros-gradient-bg min-vh-100 d-flex flex-column align-items-center justify-content-center">
        <div className="text-center">
          <CSpinner 
            color="info" 
            style={{ width: '4rem', height: '4rem' }}
          />
          <h5 className="mt-4" style={{ color: 'var(--text-secondary)' }}>Loading MiEROS...</h5>
          <small style={{ color: 'var(--text-muted)' }}>Preparing your experience</small>
        </div>
      </div>
    );
  }

  return (
    <div className="mieros-gradient-bg min-vh-100 d-flex flex-column align-items-center justify-content-center">
      {/* Top banner */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.15), rgba(124, 58, 237, 0.15))',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: 'var(--electric-blue)',
        textAlign: 'center',
        padding: '8px 12px',
        zIndex: 2000,
        fontWeight: 600,
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
        fontSize: '0.85rem',
        letterSpacing: '0.02em',
      }}>
        We are working hard to get the site ready in 2026 — thank you for your patience.
      </div>

      {/* Site unlock modal (requires password to access login) */}
      <CModal visible={siteLocked} backdrop="static" keyboard={false} alignment="center" size="sm">
        <CModalHeader>
          <CModalTitle>Site Access</CModalTitle>
        </CModalHeader>
        <CModalBody className="text-center">
          <p>Please enter the access password to continue to the login page.</p>
          <input
            type="password"
            value={unlockPassword}
            onChange={(e) => setUnlockPassword(e.target.value)}
            placeholder="Enter site password"
            style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)' }}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <CButton color="primary" onClick={() => {
              if (unlockPassword === LOCK_PASSWORD) {
                setSiteLocked(false);
                toast.success('Access granted');
              } else {
                toast.error('Incorrect password');
              }
            }}>Unlock</CButton>
            <CButton color="secondary" onClick={() => {
              setUnlockPassword('');
            }}>Clear</CButton>
          </div>
        </CModalBody>
      </CModal>

      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" />
        ))}

        {[...Array(20)].map((_, i) => (
          <div key={i + 20} className="particle" />
        ))}

        {[...Array(20)].map((_, i) => (
          <div key={i + 40} className="particle" />
        ))}

        {[...Array(15)].map((_, i) => (
          <div key={i + 60} className="particle diagonal-stream" />
        ))}

        {[...Array(15)].map((_, i) => (
          <div key={i + 75} className="particle horizontal-stream" />
        ))}

        {[...Array(15)].map((_, i) => (
          <div key={i + 90} className="particle vertical-stream" />
        ))}
      </div>

      <div className="login-circle-container">
        {/* Center logo */}
        <div className="center-logo">
          <img src={logo} alt="Logo" />
        </div>
        
        {/* Circular login buttons */}
        <div className="login-circle-buttons">
          <CButton 
            onClick={handleGoogleLogin} 
            color="link" 
            className="circle-button google-btn"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={gl} alt="Google Sign In" className="button-icon" />
          </CButton>
          
          <CButton 
            onClick={handleTwitterLogin} 
            color="link" 
            className="circle-button twitter-btn"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={cibTwitter} alt="X" className="button-icon" />
          </CButton>
          
          {/* <CButton 
            onClick={handleFacebookLogin} 
            color="link" 
            className="circle-button facebook-btn"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={fbIcon} alt="Facebook" className="button-icon" />
          </CButton> */}
          
          <CButton 
            onClick={handleAppleLogin} 
            color="link" 
            className="circle-button apple-btn"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={al} alt="Apple Sign In" className="button-icon" />
          </CButton>
          
          {/* <CButton 
            onClick={() => navigate('/local-login')} 
            color="link" 
            className="circle-button email-btn"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={emailIcon} alt="Email Sign In" className="button-icon" />
          </CButton> */}
        </div>
      </div>

      <div style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        fontSize: '12px',
        color: 'var(--text-muted)',
        opacity: 0.8,
        zIndex: 1000,
        pointerEvents: 'none'
      }}>
        v{import.meta.env.VITE_VERSION || version || '1.0.0'}
        {config.environment === 'development' && (
          <div>Development Environment</div>
        )}
      </div>

      {/* Registration Progress Overlay */}
      {registrationProgress.isRegistering && (
        <CModal 
          visible={true} 
          backdrop="static" 
          keyboard={false}
          alignment="center"
          size="sm"
        >
          <CModalBody className="text-center p-4">
            <div className="registration-progress">
              <div className="mb-3">
                <CSpinner 
                  color="primary" 
                  style={{ width: '3rem', height: '3rem' }}
                />
              </div>
              
              <h5 className="mb-3">Setting up your account...</h5>
              
              <div className="progress-info mb-3">
                <small className="mb-2 d-block mb-2">
                  {registrationProgress.currentStep}
                </small>
                
                <CProgress 
                  value={registrationProgress.progress} 
                  className="mb-2"
                  style={{ height: '8px' }}
                />
                
                <small className="mb-2">
                  {Math.round(registrationProgress.progress)}% Complete
                </small>
              </div>
              
              <div className="registration-steps">
                {registrationProgress.steps.map((step, index) => {
                  const isCompleted = index < (registrationProgress.progress / 100) * registrationProgress.steps.length;
                  const isCurrent = Math.floor((registrationProgress.progress / 100) * registrationProgress.steps.length) === index;
                  
                  return (
                    <div 
                      key={index} 
                      className={`step-item d-flex align-items-center mb-2 ${
                        isCompleted ? 'text-success' : isCurrent ? 'text-primary' : 'mb-2'
                      }`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <div className="step-indicator me-2">
                        {isCompleted ? (
                          <CBadge color="success" shape="rounded-pill">✓</CBadge>
                        ) : isCurrent ? (
                          <CSpinner size="sm" color="primary" />
                        ) : (
                          <CBadge color="light" shape="rounded-pill">{index + 1}</CBadge>
                        )}
                      </div>
                      <span className={isCompleted ? 'text-decoration-line-through' : ''}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-3">
                <small className="text-info d-block">
                  🔒 Please don't refresh or close this page
                </small>
                <small className="mb-2">
                  This process usually takes 10-30 seconds
                </small>
              </div>
            </div>
          </CModalBody>
        </CModal>
      )}
    </div>
  );

};

export default withFirebase(Login);



